import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Eye, EyeOff, Shield, ChevronDown, ChevronUp, Info, Gift } from 'lucide-react';
import Logo from '../../components/Logo';
import iosFetch, { iosPost } from '../../utils/iosFetch';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: searchParams.get('ref') || ''
  });
  const [kycData, setKycData] = useState({
    full_name: '',
    dob: '',
    country: '',
    phone: '',
    id_type: 'national_id',
    id_number: '',
    address: '',
    city: '',
    postal_code: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showKycSection, setShowKycSection] = useState(false);
  const [kycRequired, setKycRequired] = useState(false);
  const [kycEnabled, setKycEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const BACKEND_URL = '';

  useEffect(() => {
    // Fetch KYC requirements to check if KYC is enabled
    fetchKycSettings();
  }, []);

  const fetchKycSettings = async () => {
    try {
      const response = await iosFetch(`${BACKEND_URL}/api/platform/config/kyc-thresholds`);
      if (response.ok) {
        const data = await response.json();
        const requiresKyc = data.thresholds?.requires_kyc || false;
        setKycRequired(requiresKyc);
        setKycEnabled(true); // KYC feature is enabled if config exists
      }
    } catch (error) {
      console.log('KYC settings not available, showing KYC as optional');
      setKycEnabled(true); // Show KYC section even if config fetch fails
      setKycRequired(false); // Make it optional by default
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleKycChange = (e) => {
    setKycData({ ...kycData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    // Validate KYC if required
    if (kycRequired && showKycSection) {
      if (!kycData.full_name || !kycData.dob || !kycData.country || !kycData.phone) {
        toast({ 
          title: 'KYC Required', 
          description: 'Please fill all required KYC fields',
          variant: 'destructive' 
        });
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const { authAPI } = await import('../../services/api');
      const registrationData = {
        username: formData.username,
        email: formData.email,
        password: formData.password
      };

      // Add referral code if provided
      if (formData.referralCode && formData.referralCode.trim()) {
        registrationData.referral_code = formData.referralCode.trim();
      }

      // Add KYC data if provided
      if (showKycSection && kycData.full_name) {
        registrationData.kyc_data = kycData;
      }

      await authAPI.register(registrationData);
      
      toast({ 
        title: 'Registration successful!', 
        description: showKycSection && kycData.full_name 
          ? 'Account created with KYC submitted. Please log in.' 
          : 'Please log in with your credentials' 
      });
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-cyan-500/20">
        <button onClick={() => navigate('/login')} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-black">BACK</span>
        </button>
        <Logo size="small" showText={true} />
        <div className="w-16"></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-black text-cyan-400 mb-2">CREATE ACCOUNT</h1>
            <p className="text-cyan-600 font-bold">JOIN EDGE TRADE TODAY</p>
          </div>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-cyan-400">Username</Label>
              <Input
                id="username"
                name="username"
                type="text"
                placeholder="Choose a username"
                value={formData.username}
                onChange={handleChange}
                required
                className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-cyan-400">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-cyan-400">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-cyan-400">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Referral Code Section */}
            <div className="space-y-2">
              <Label htmlFor="referralCode" className="text-cyan-400 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Referral Code (Optional)
              </Label>
              <Input
                id="referralCode"
                name="referralCode"
                type="text"
                placeholder="Enter referral code"
                value={formData.referralCode}
                onChange={handleChange}
                className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50 uppercase"
              />
              {formData.referralCode && (
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <Gift className="w-3 h-3" />
                  Earn rewards! Get $10 on first $1000 deposit + ongoing 1% commission
                </p>
              )}
            </div>

            {/* KYC Section - Optional */}
            {kycEnabled && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowKycSection(!showKycSection)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-lg hover:border-cyan-500/50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    <div className="text-left">
                      <p className="text-cyan-400 font-semibold">
                        {kycRequired ? 'KYC Verification (Required)' : 'KYC Verification (Optional)'}
                      </p>
                      <p className="text-cyan-600 text-sm">
                        {kycRequired 
                          ? 'Complete now to unlock all features' 
                          : 'Skip now, complete later from your profile'}
                      </p>
                    </div>
                  </div>
                  {showKycSection ? (
                    <ChevronUp className="w-5 h-5 text-cyan-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-cyan-400" />
                  )}
                </button>

                {showKycSection && (
                  <div className="mt-4 space-y-4 p-4 bg-slate-800/30 border border-cyan-500/20 rounded-lg">
                    <div className="flex items-start gap-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                      <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-blue-300 text-sm">
                        Providing KYC information during registration helps us verify your identity faster.
                        {!kycRequired && ' You can skip this and complete it later.'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="full_name" className="text-cyan-400">
                          Full Name {kycRequired && <span className="text-red-400">*</span>}
                        </Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          type="text"
                          placeholder="John Doe"
                          value={kycData.full_name}
                          onChange={handleKycChange}
                          required={kycRequired}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dob" className="text-cyan-400">
                          Date of Birth {kycRequired && <span className="text-red-400">*</span>}
                        </Label>
                        <Input
                          id="dob"
                          name="dob"
                          type="date"
                          value={kycData.dob}
                          onChange={handleKycChange}
                          required={kycRequired}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-cyan-400">
                          Country {kycRequired && <span className="text-red-400">*</span>}
                        </Label>
                        <Input
                          id="country"
                          name="country"
                          type="text"
                          placeholder="United States"
                          value={kycData.country}
                          onChange={handleKycChange}
                          required={kycRequired}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-cyan-400">
                          Phone Number {kycRequired && <span className="text-red-400">*</span>}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+1 234 567 8900"
                          value={kycData.phone}
                          onChange={handleKycChange}
                          required={kycRequired}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="id_type" className="text-cyan-400">ID Type</Label>
                        <select
                          id="id_type"
                          name="id_type"
                          value={kycData.id_type}
                          onChange={handleKycChange}
                          className="w-full h-10 px-3 bg-slate-800/50 border border-cyan-500/30 rounded-md text-cyan-100"
                        >
                          <option value="national_id">National ID</option>
                          <option value="passport">Passport</option>
                          <option value="drivers_license">Driver's License</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="id_number" className="text-cyan-400">ID Number</Label>
                        <Input
                          id="id_number"
                          name="id_number"
                          type="text"
                          placeholder="ID123456789"
                          value={kycData.id_number}
                          onChange={handleKycChange}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="address" className="text-cyan-400">Street Address</Label>
                        <Input
                          id="address"
                          name="address"
                          type="text"
                          placeholder="123 Main Street"
                          value={kycData.address}
                          onChange={handleKycChange}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-cyan-400">City</Label>
                        <Input
                          id="city"
                          name="city"
                          type="text"
                          placeholder="New York"
                          value={kycData.city}
                          onChange={handleKycChange}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="postal_code" className="text-cyan-400">Postal Code</Label>
                        <Input
                          id="postal_code"
                          name="postal_code"
                          type="text"
                          placeholder="10001"
                          value={kycData.postal_code}
                          onChange={handleKycChange}
                          className="h-10 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-full mt-6 neon-button"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Register'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;